import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';

import { DialogCustom } from './dialog-custom';

interface TimePickerComponentProps {
  modelValue: { value: unknown }; // modelValue from editor
  onUpdateModelValue?: (value: string) => void; // update:modelValue in Vue
  onFinished?: () => void; // finished in Vue
  cellElement?: HTMLElement; // Cell element for positioning
}

export const TimePickerComponent: React.FC<TimePickerComponentProps> = ({
  modelValue,
  onUpdateModelValue,
  onFinished,
  cellElement,
}) => {
  // Get value from modelValue
  const value = typeof modelValue?.value === 'string' ? modelValue.value : '';
  // Parse initial value
  const [selectedHour, setSelectedHour] = useState<number>();
  const [selectedMinute, setSelectedMinute] = useState<number>();

  useEffect(() => {
    if (value) {
      const times = value.split(':');
      const hour = times[0];
      const minute = times[1];
      setSelectedHour(hour ? Number(hour) : 1);
      setSelectedMinute(minute ? Number(minute) : 0);
    }
  }, [value]);

  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  // Generate hour options (0-23)
  const hours = Array.from({ length: 29 }, (_, i) => i + 1);

  // Generate minute options (0-59 in 5-minute increments)
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  // Scroll to selected item on mount and when selection changes
  useEffect(() => {
    if (hourScrollRef.current) {
      const selectedElement = hourScrollRef.current.querySelector(`[data-hour="${selectedHour}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedHour]);

  useEffect(() => {
    if (minuteScrollRef.current) {
      const selectedElement = minuteScrollRef.current.querySelector(
        `[data-minute="${selectedMinute}"]`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedMinute]);

  const handleHourClick = (hour: number) => {
    setSelectedHour(hour);
    let minute = selectedMinute ?? 0;
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onUpdateModelValue?.(timeStr);
  };

  const handleMinuteClick = (minute: number) => {
    setSelectedMinute(minute);
    let hour = selectedHour ?? 1;
    const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    onUpdateModelValue?.(timeStr);
  };

  const handleOk = () => {
    let timeStr = '';
    if (selectedHour) {
      timeStr = `${String(selectedHour).padStart(2, '0')}:${String(selectedMinute).padStart(2, '0')}`;
    }
    onUpdateModelValue?.(timeStr);
    onFinished?.();
  };

  const timePickerBox = (
    <DialogCustom cellElement={cellElement} onFinished={onFinished}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingX: '16px',
          marginBottom: '8px',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            width: 56,
            textAlign: 'center',
            fontWeight: 500,
            color: '#1A1C20',
          }}
        >
          時
        </Typography>
        <Typography
          variant='body2'
          sx={{
            width: 56,
            textAlign: 'center',
            fontWeight: 500,
            color: '#1A1C20',
          }}
        >
          分
        </Typography>
      </Box>

      {/* Time Selection Columns */}
      <Box
        sx={{
          display: 'flex',
          marginBottom: '16px',
        }}
      >
        {/* Hour Column */}
        <Box
          ref={hourScrollRef}
          sx={{
            flex: 1,
            maxHeight: '200px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Opera
            },
          }}
        >
          {hours.map((hour) => (
            <Box
              key={hour}
              data-hour={hour}
              onClick={() => handleHourClick(hour)}
              sx={{
                minHeight: '40px',
                minWidth: '72px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                cursor: 'pointer',
                marginY: '4px',
                backgroundColor: selectedHour === hour ? '#0047BA' : 'transparent',
                color: selectedHour === hour ? '#FFFFFF' : '#1A1C20',
                fontWeight: selectedHour === hour ? 600 : 400,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: selectedHour === hour ? '#0047BA' : '#F5F5F5',
                },
              }}
            >
              <Typography variant='body1'>{String(hour).padStart(2, '0')}</Typography>
            </Box>
          ))}
        </Box>

        {/* Minute Column */}
        <Box
          ref={minuteScrollRef}
          sx={{
            flex: 1,
            maxHeight: '200px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Opera
            },
          }}
        >
          {minutes.map((minute) => (
            <Box
              key={minute}
              data-minute={minute}
              onClick={() => handleMinuteClick(minute)}
              sx={{
                minHeight: '40px',
                minWidth: '72px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '20px',
                cursor: 'pointer',
                marginY: '4px',
                backgroundColor: selectedMinute === minute ? '#0047BA' : 'transparent',
                color: selectedMinute === minute ? '#FFFFFF' : '#1A1C20',
                fontWeight: selectedMinute === minute ? 600 : 400,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: selectedMinute === minute ? '#0047BA' : '#F5F5F5',
                },
              }}
            >
              <Typography variant='body1'>{String(minute).padStart(2, '0')}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* OK Button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant='contained'
          onClick={handleOk}
          sx={{
            color: '#0047BA',
            backgroundColor: '#FFFFFF',
            boxShadow: 'none',
            textTransform: 'none',
            borderRadius: '4px',
            paddingX: '24px',
            paddingY: '6px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#FFFFFF',
              boxShadow: 'none',
            },
          }}
        >
          OK
        </Button>
      </Box>
    </DialogCustom>
  );

  // Render directly (not using Portal) to maintain event handling with Handsontable
  // High z-index and isolation ensure it appears above Handsontable headers
  return timePickerBox;
};
